import CheckmarkOutline32 from '@carbon/icons-react/es/checkmark--outline/32';
import Locked20 from '@carbon/icons-react/es/locked/20';
import Misuse20 from '@carbon/icons-react/es/misuse/20';
import { useOktaAuth } from '@okta/okta-react';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import { redirectToSavedTab } from 'hooks/use-login-with-token';
import { useTranslation } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import { CompanyLogo } from 'routes/auth/common/company-logo';
import { ApiKey, getOkta, logger } from 'services';
import { getTokenMeta } from 'utils/token';
import {
  Container,
  Description,
  EmailSentToDetail,
  EmailText,
  EmailTextHolder,
  Footer,
  Heading,
  InputError,
  OTPContainer,
  OTPInput,
  Prompt,
  ResendButton,
  SignInButton,
  SubHeading,
} from './style';
import { triggerOtp, validateOtp } from './utils';

export const TwoFactAuthPage = (token) => {
  const placeholderText = useTranslation('magic_link.otp.input.placeholder');
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);
  const { oktaAuth } = useOktaAuth();
  const history = useHistory();
  const currentPage = useRef(false);
  const setApiKeyInStorage = (apiKeyData) => {
    const okta = getOkta();

    const api_key = [
      'api_key',
      apiKeyData?.apiKey,
      apiKeyData?.createdBy?.userId,
      apiKeyData?.apiKeyId,
    ];

    localStorage.setItem('__api_key', JSON.stringify(api_key));
    okta?.tokenManager.clear();
    oktaAuth.authStateManager.updateAuthState();
  };
  const { data, isLoading, isError } = useAccountMetaQuery();

  const triggerOtpHandler = async (tokenValue) => {
    try {
      await triggerOtp(tokenValue);
      toastService({
        title: 'Code resent',
        message: 'Verifcation code has been successfully resent',
      }).success();
    } catch {
      logger.log('OTP trigger failed');
    }
  };
  const validateOtpHandler = async (
    tokenValue,
    otpInput,
    taskStatusUpdateAction,
  ) => {
    try {
      currentPage.current = true;
      const response = await validateOtp(tokenValue, otpInput);

      setApiKeyInStorage(response);
      if (taskStatusUpdateAction.otpEnabled) {
        axios
          .post<string, { data: { taskName: string } }>(
            taskStatusUpdateAction.apiUrl,
          )
          .then(() => {
            window.open(taskStatusUpdateAction.redirectUrl, '_self');
          });
      } else {
        redirectToSavedTab(history);
      }
    } catch {
      setIsCodeInvalid(true);
    }
  };

  const {
    token: {
      location: { state },
    },
  } = token;

  const tokenValue = state.token;
  const taskStatusUpdateAction = state.taskStatusUpdateAction ?? {};

  const [otp, setOtp] = useState('');
  const { decoded } = getTokenMeta(tokenValue);

  useEffect(() => {
    const loginWithStorageListener = () => {
      if (currentPage.current) {
        return;
      }
      if (ApiKey.exists()) {
        const okta = getOkta();

        okta?.tokenManager.clear();
        oktaAuth.authStateManager.updateAuthState();
        redirectToSavedTab(history);
      }
    };

    window.addEventListener('storage', loginWithStorageListener);

    return () =>
      window.removeEventListener('storage', loginWithStorageListener);
  }, [history, oktaAuth.authStateManager]);

  return isLoading ? (
    <PageLoading />
  ) : data && !isError ? (
    <Layout>
      <Container>
        <div>
          {data && <CompanyLogo data={data.company} />}
          <Heading>
            <FormattedMessage
              id="signin.title"
              values={{
                companyName: data.company.companyName,
              }}
            />
          </Heading>
        </div>
        <Description>
          <SubHeading>
            <FormattedMessage id="twofactor_authentication.title" />
          </SubHeading>
          <EmailSentToDetail>
            <CheckmarkOutline32 color="var(--green-50)" />
            <EmailTextHolder>
              <FormattedMessage id="twofactor_authentication.message" />
              <EmailText>{decoded?.emailId ?? ''}</EmailText>
            </EmailTextHolder>
          </EmailSentToDetail>
        </Description>
        <OTPContainer>
          <Prompt>
            <FormattedMessage id="twofactor_authentication.prompt" />
          </Prompt>

          <OTPInput
            isError={isCodeInvalid}
            onFocus={() => setIsCodeInvalid(false)}
            onChange={(e) => {
              setOtp(e.target.value);
            }}
            autoFocus
            placeholder={placeholderText}
            onPressEnter={() => {
              validateOtpHandler(tokenValue, otp, taskStatusUpdateAction);
            }}
          />
          {isCodeInvalid && (
            <InputError>
              <Misuse20
                style={{
                  color: 'var(--error-color)',
                  verticalAlign: 'center',
                  marginRight: 'var(--spacing-02)',
                }}
              />
              <FormattedMessage id="magic_link.otp.invalid.error" />
            </InputError>
          )}
        </OTPContainer>
        <SignInButton
          onClick={() => {
            validateOtpHandler(tokenValue, otp, taskStatusUpdateAction);
          }}
          type="primary"
          icon={<Locked20 />}
        >
          <FormattedMessage id="magic_link.otp.sigin.button.text" />
        </SignInButton>
        <Footer>
          <ResendButton
            size="small"
            type="text"
            onClick={() => triggerOtpHandler(tokenValue)}
          >
            <FormattedMessage id="magic_link.otp.resend.button.text" />
          </ResendButton>
        </Footer>
      </Container>
    </Layout>
  ) : (
    <></>
  );
};
